/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { format } from 'date-fns';
import type { TFunction } from 'i18next';

import {
  SearchAggregatorResult,
  CurrencyListData,
  FlightListingDeepLinkParams,
} from '../types/FlightListing.types';
import type { UpsellBundlePriceDetails } from '../types/upsellBundle.types';
import { ANALYTICS_CONSTANTS, SCREEN_NAME, GTM_LOB } from '../config/analytics';
import type {
  FlightSearchParamsWithSelectedFlight,
  NativeViewListingItem,
  SearchParams,
  SelectFlightAnalyticsParams,
  ThinListingViewBundlesParams,
  ViewListingParams,
  WebViewListingItem,
} from '../analytics/flightListing/flightListing.meta';
import type { SearchFlightFilters } from '../hooks/flights';
import type { FareItem } from '../types/lowFare.types';
import { TripType } from '../types/FlightSearch.types';
import {
  Trip,
  FlightDetailsType,
  DepartReturn,
  Segment,
} from '../widgets/FlightCard/types';
import { getStops, getStopsLabel, getTime } from '../widgets/FlightCard/utils';
import {
  calculateRouteType,
  getJourneyDuration,
  stationOrCityCodeOrName,
} from './FlightSearch.utils';
import { formatFlightLocalDate } from './dateUtil';
import {
  TripFlowType,
  TripFlowTypeValue,
  SegmentType,
  PROVIDERS,
  AIRASIA_CODES,
  BUNDLE_ID,
} from '../config/constants';
import { stringToBool } from './FlightSearch.utils';
import { AIRLINE_PROFILE } from '../config/constants';

const FLIGHT_LISTING_CATEGORY = 'Air Tickets';
const RECOMMENDED_FLIGHTS_LIST = 'Best departing flights';
const OTHER_FLIGHTS_LIST = 'Other departing flights';
const SELECT_FLIGHT_LIST = 'OTA Flight Search Results - Best';

const toTitleCase = (value?: string | null) => {
  if (!value) return '';

  return value
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
};

const formatAnalyticsPrice = (value?: number | null) => {
  const numericValue = Number(value ?? 0);

  if (!Number.isFinite(numericValue)) return '0.00';

  return numericValue.toFixed(2);
};

const formatAnalyticsDate = (value?: string | Date | null) => {
  if (!value) return '';

  const date = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(date.getTime())) return '';

  return format(date, ANALYTICS_CONSTANTS.DATE_FORMAT);
};

const formatDurationMinutes = (duration?: number | null) => {
  const totalSeconds = Number(duration ?? 0);

  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) return '0 minutes';

  return `${Math.round(totalSeconds / 60)} minutes`;
};

const buildStopsText = (stops: number) => {
  if (stops <= 0) return 'Direct';
  if (stops === 1) return '1 Stop';

  return `${stops} Stops`;
};

const getPrimarySegment = (trip: Trip) => {
  const segments =
    trip.flightDetails?.segments ?? trip.flightDetails?.depart?.segments;
  return segments?.[0];
};

const getMarketingFlightNumber = (segment?: Segment) =>
  segment?.marketingFlightNo ??
  segment?.operatingFlightNo ??
  segment?.flightNumber ??
  '';

const getFlightCode = (segment?: Segment, trip?: Trip) => {
  const resolvedSegment = segment ?? getPrimarySegment(trip as Trip);
  const carrierCode =
    resolvedSegment?.marketingCarrierCode ??
    resolvedSegment?.carrierCode ??
    resolvedSegment?.airline ??
    '';
  const flightNumber = getMarketingFlightNumber(resolvedSegment);

  return `${carrierCode}${flightNumber}`;
};

const getProviderVariant = (trip: Trip) =>
  toTitleCase(
    trip.airlineProfile !== AIRLINE_PROFILE.GOQUO
      ? GTM_LOB[
          (trip.airlineProfile?.toUpperCase() || '') as keyof typeof GTM_LOB
        ]
      : trip.provider,
  );

const PROMO_KEYWORD_TYPE = 'PROMO';

const getPromoFareCategory = (trip: Trip) =>
  trip.keywords?.some((keyword) => keyword?.type === PROMO_KEYWORD_TYPE)
    ? PROMO_KEYWORD_TYPE
    : '';

const buildWebViewListingProducts = ({
  trip,
  searchParams,
  startPosition,
}: {
  trip: Trip;
  searchParams: SearchParams;
  startPosition: number;
}): WebViewListingItem[] => {
  const segments = trip.flightDetails?.segments ?? [
    ...(trip.flightDetails?.depart?.segments ?? []),
    ...(trip.flightDetails?.return?.segments ?? []),
  ];
  const connectionType = segments.length > 1 ? 'Fly-Through' : 'Direct';

  return segments
    .slice(0, 11)
    .map((segment, index) => {
      const departureStation =
        segment?.designator?.departureStation ?? searchParams.originairport;
      const arrivalStation =
        segment?.designator?.arrivalStation ?? searchParams.destinationairport;
      const routeName = `${departureStation}-${arrivalStation}`;
      const position = startPosition + index;

      return {
        id: getFlightCode(segment, trip),
        name: routeName,
        category: FLIGHT_LISTING_CATEGORY,
        brand:
          segment?.airline ??
          segment?.marketingCarrierCode ??
          segment?.carrierCode ??
          '',
        variant: getProviderVariant(trip),
        quantity: '1',
        price: formatAnalyticsPrice(trip.convertedPrice ?? trip.price),
        position,
        dimension1: searchParams.flighttype,
        dimension2: departureStation,
        dimension3: arrivalStation,
        dimension4:
          segment?.fareClassCategory ??
          segment?.fareType ??
          segment?.fareClass ??
          '',
        dimension5: formatAnalyticsDate(
          segment?.designator?.departureTime ??
            segment?.designator?.departureTimeUtc,
        ),
        dimension6: `Flight ${position}`,
        dimension8: searchParams.lengthofstay,
        dimension9: searchParams.adult,
        dimension10: searchParams.totalpax,
        dimension13: routeName,
        dimension15:
          connectionType === 'Fly-Through'
            ? connectionType
            : buildStopsText(getStops(trip.flightDetails)),
        dimension16: getFlightCode(segment, trip),
        dimension42: searchParams.journeytype,
        dimension61: formatDurationMinutes(segment?.duration),
        list: trip.sponsored ? RECOMMENDED_FLIGHTS_LIST : OTHER_FLIGHTS_LIST,
      };
    })
    .filter(Boolean);
};

const buildNativeViewListingProduct = (
  searchParams: SearchParams,
): NativeViewListingItem => ({
  ...searchParams,
  screenName: SCREEN_NAME.flightSearchList,
  lineofbusiness: ANALYTICS_CONSTANTS.OTA,
});

const resolveProductPosition = (trip: Trip, fallbackPosition: number) => {
  const sid = Number(trip.sId);

  return Number.isFinite(sid) && sid > 0 ? sid : fallbackPosition;
};

const buildSelectFlightWebProducts = ({
  trips,
  searchParams,
}: {
  trips: Trip[];
  searchParams: SearchParams;
}): WebViewListingItem[] =>
  trips.flatMap((trip, index) =>
    buildWebViewListingProducts({
      trip,
      startPosition: resolveProductPosition(trip, index + 1),
      searchParams,
    }).map((product) => ({
      ...product,
      list: SELECT_FLIGHT_LIST,
    })),
  );

export const buildFlightsList = (
  searchAgg: SearchAggregatorResult | null | undefined,
  hasSelectedDepart: boolean,
  type: TripFlowTypeValue,
): Trip[] => {
  if (!searchAgg?.searchResults) return [];

  const trips = searchAgg.searchResults.trips ?? [];
  const recommendedFlights = searchAgg.searchResults.recommendedFlights ?? [];
  if (type === TripFlowType.BUNDLED) {
    const sponsored = (recommendedFlights as Trip[]).map((f) => ({
      ...f,
      sponsored: true,
    }));
    const regular = (trips as Trip[]).map((f) => ({
      ...f,
      sponsored: false,
    }));
    return [...sponsored, ...regular];
  }

  const tripIndex = hasSelectedDepart ? 1 : 0;

  const sponsored =
    recommendedFlights?.[tripIndex]?.flightsList?.map((f: Trip) => ({
      ...f,
      sponsored: true,
    })) ?? [];

  const regular =
    trips?.[tripIndex]?.flightsList?.map((f: Trip) => ({
      ...f,
      sponsored: false,
    })) ?? [];

  return [...sponsored, ...regular];
};

export const buildDepartData = (
  selectedDepartFlight?: Trip | null,
  currencyList: CurrencyListData = {},
  t?: TFunction,
  totalGuests = 1,
) => {
  if (!selectedDepartFlight?.flightDetails?.designator || !t) return null;

  const { designator } = selectedDepartFlight.flightDetails;
  const stopsLabel = getStopsLabel(
    getStops(selectedDepartFlight.flightDetails),
    t,
  );

  return {
    departTime: getTime(designator.departureTime) ?? '',
    arrivalTime: getTime(designator.arrivalTime) ?? '',
    dividerText: stopsLabel ?? '',
    departText: t('flightsListingPage.selected_depart_flight'),
    currency: currencyList.userCurrency ?? 'MYR',
    price: getSelectedDepartFlightTotalPrice(
      selectedDepartFlight,
      currencyList as CurrencyListData,
    ),
    departDate: formatFlightLocalDate(
      designator.departureTime ?? designator.departureTimeUtc,
      'EEE, d MMM',
    ),
    carrier:
      selectedDepartFlight.flightDetails.segments?.[0]?.marketingAirlineName,
    bundle: selectedDepartFlight.selectedBundle?.title,
    priceSubtitle: buildPricePerGuestSubtitle(t, totalGuests),
    redemptionPrice:
      selectedDepartFlight?.redemptionPrice != null
        ? Math.ceil(selectedDepartFlight.redemptionPrice)
        : null,
    redemptionPoints: selectedDepartFlight.redemptionPoints,
    selectedBundle:
      selectedDepartFlight?.selectedBundle?.bundlePriceDetails
        ?.bundlePriceAllPax,
    convertedDiscountValue:
      selectedDepartFlight.selectedBundle?.bundleId !== BUNDLE_ID.BASIC
        ? selectedDepartFlight.convertedDiscountValue
        : null,
  };
};

export const getSelectedDepartFlightTotalPrice = (
  selectedDepartFlight?: Trip | null,
  currencyList: CurrencyListData = {},
) => {
  if (!selectedDepartFlight) return 0;

  const airlineProfile = selectedDepartFlight.airlineProfile ?? '';
  const baseFare = getConvertedPrice(
    selectedDepartFlight.price ?? selectedDepartFlight.convertedPrice ?? 0,
    selectedDepartFlight.currencyCode ?? '',
    airlineProfile,
    currencyList,
  );
  const convertedBundlePriceDetails = convertBundlePriceDetails(
    selectedDepartFlight.selectedBundle?.bundlePriceDetails,
    selectedDepartFlight.selectedBundle?.currency ??
      selectedDepartFlight.currencyCode ??
      '',
    airlineProfile,
    currencyList,
  );
  const selectedBundleFare = getConvertedPrice(
    selectedDepartFlight.selectedBundle?.priceValue ?? 0,
    selectedDepartFlight.selectedBundle?.currency ?? '',
    airlineProfile,
    currencyList,
  );

  return resolveBundleTotalPrice(
    convertedBundlePriceDetails?.totalFareWithBundleAllPax,
    baseFare,
    selectedBundleFare,
  );
};

export const resolveBundleTotalPrice = (
  totalFareWithBundleAllPax?: number,
  baseFare = 0,
  bundleFare = 0,
) => {
  const fallbackTotal =
    (Number.isFinite(baseFare) ? baseFare : 0) +
    (Number.isFinite(bundleFare) ? bundleFare : 0);
  const normalizedTotalFare = Number.isFinite(totalFareWithBundleAllPax)
    ? Number(totalFareWithBundleAllPax)
    : undefined;
  const validTotalFare =
    normalizedTotalFare !== undefined && normalizedTotalFare > 0
      ? normalizedTotalFare
      : undefined;

  return Math.ceil(validTotalFare ?? fallbackTotal);
};

export const buildSegmentFlightDetails = (
  base: DepartReturn | FlightDetailsType | undefined,
  parent: FlightDetailsType,
  segmentType: SegmentType,
): FlightDetailsType | undefined => {
  if (!base) return undefined;

  let normalizedDuration = 0;
  if (typeof parent?.duration === 'number') {
    normalizedDuration = parent.duration;
  } else if (parent?.duration && typeof parent?.duration === 'object') {
    normalizedDuration =
      segmentType === SegmentType.DEPARTURE
        ? parent.duration.departure
        : (parent.duration.return ?? 0);
  }

  return {
    designator: base.designator,
    segments: base.segments,
    totalDaysCount: base.totalDaysCount,
    baggage: base?.baggage ?? parent?.baggage,
    duration: normalizedDuration,
    layoverTime: parent?.layoverTime,
    numberOfStops: parent?.numberOfStops ?? {
      departure: 0,
      return: 0,
      total: 0,
    },

    totalCarbonEmissionInKiloGrams:
      parent?.totalCarbonEmissionInKiloGrams ??
      (base?.segments ?? []).reduce(
        (sum, s) => sum + (s?.carbonEmissionInKiloGrams ?? 0),
        0,
      ),
  };
};

/**
 * Converts a flight price from the airline's base currency to the user's display currency.
 * * This function performs a contextual lookup based on the airline profile to find
 * specific markup and conversion factors. It traverses the nested `conversionFactors`
 * object to locate the source currency and the user's target currency rate.
 *
 * @param price - The raw price value (numeric or string) from the API.
 * @param currencyCode - The source currency code (e.g., 'EUR', 'USD').
 * @param airlineProfile - The carrier identifier (e.g., 'dotrez', 'vietjet').
 * @param list - The search aggregator results containing markup and user currency settings.
 * @returns The converted price as a number, or the original price if no conversion factor is found.
 */
export const getConvertedPrice = (
  price: number | string,
  currencyCode: string,
  airlineProfile: string = '',
  list: CurrencyListData = {},
): number => {
  const numericPrice = Number(price);
  if (isNaN(numericPrice)) return 0;

  const markupList = list.markup || [];
  const targetCurrency = list.userCurrency;

  const currencyConfig = markupList.find(
    (markup) =>
      markup.airlineProfile.toLowerCase() === airlineProfile.toLowerCase(),
  );

  const sourceFactors = currencyConfig?.conversionFactors?.[currencyCode];

  const conversionRate = sourceFactors?.[targetCurrency || ''];

  if (typeof conversionRate === 'number') {
    return numericPrice * conversionRate;
  }

  return parseFloat(numericPrice.toFixed(2));
};

export const convertBundlePriceDetails = (
  bundlePriceDetails?: UpsellBundlePriceDetails,
  currencyCode: string = '',
  airlineProfile: string = '',
  list: CurrencyListData = {},
): UpsellBundlePriceDetails | undefined => {
  if (!bundlePriceDetails) return undefined;

  return {
    bundlePricePerPax:
      bundlePriceDetails.bundlePricePerPax !== undefined
        ? getConvertedPrice(
            bundlePriceDetails.bundlePricePerPax,
            currencyCode,
            airlineProfile,
            list,
          )
        : undefined,
    bundlePriceAllPax:
      bundlePriceDetails.bundlePriceAllPax !== undefined
        ? getConvertedPrice(
            bundlePriceDetails.bundlePriceAllPax,
            currencyCode,
            airlineProfile,
            list,
          )
        : undefined,
    totalFareWithBundlePerPax:
      bundlePriceDetails.totalFareWithBundlePerPax !== undefined
        ? getConvertedPrice(
            bundlePriceDetails.totalFareWithBundlePerPax,
            currencyCode,
            airlineProfile,
            list,
          )
        : undefined,
    totalFareWithBundleAllPax:
      bundlePriceDetails.totalFareWithBundleAllPax !== undefined
        ? getConvertedPrice(
            bundlePriceDetails.totalFareWithBundleAllPax,
            currencyCode,
            airlineProfile,
            list,
          )
        : undefined,
  };
};

export const buildViewListingAnalytics = (
  filters: SearchFlightFilters,
  currency: string,
  stations?: Record<string, { CountryCode?: string }>,
): SearchParams | null => {
  if (!filters.originObj || !filters.destinationObj) return null;

  const originairport = stationOrCityCodeOrName(filters.originObj, 'code');
  const destinationairport = stationOrCityCodeOrName(
    filters.destinationObj,
    'code',
  );
  const getCountryCode = (
    airportCode: string,
    filterObj: { CountryCode?: string },
  ): string =>
    stations?.[airportCode]?.CountryCode ?? filterObj.CountryCode ?? '';
  const origincountry = getCountryCode(originairport, filters.originObj);
  const destinationcountry = getCountryCode(
    destinationairport,
    filters.destinationObj,
  );

  return {
    flighttype:
      filters.tripType === TripType.ROUND_TRIP
        ? ANALYTICS_CONSTANTS.RT
        : ANALYTICS_CONSTANTS.OW,
    totalpax: filters.adultCount + filters.childCount + filters.infantCount,
    adult: filters.adultCount,
    kid: filters.childCount,
    infant: filters.infantCount,
    voucher: filters.promoCode ?? '',
    journeytype:
      origincountry === destinationcountry
        ? ANALYTICS_CONSTANTS.DOMESTIC
        : ANALYTICS_CONSTANTS.INTERNATIONAL,
    departdate: filters.departDate
      ? format(filters.departDate, ANALYTICS_CONSTANTS.DATE_FORMAT)
      : '',
    returndate:
      filters.tripType === TripType.ROUND_TRIP && filters.returnDate
        ? format(filters.returnDate, ANALYTICS_CONSTANTS.DATE_FORMAT)
        : '',
    origincountry,
    destinationcountry,
    originairport,
    destinationairport,
    lengthofstay:
      filters.tripType === TripType.ROUND_TRIP
        ? getJourneyDuration(filters.returnDate, filters.departDate)
        : 0,
    currency,
  };
};

export const buildViewListingAnalyticsPayload = ({
  filters,
  currency,
  trips,
  stations,
}: {
  filters: SearchFlightFilters;
  currency: string;
  trips: Trip[];
  stations?: Record<string, { CountryCode?: string }>;
}): ViewListingParams | null => {
  const searchParams = buildViewListingAnalytics(filters, currency, stations);

  if (!searchParams) return null;

  const nativeProducts = trips.map(() =>
    buildNativeViewListingProduct(searchParams),
  );
  let nextPosition = 1;
  const webProducts = trips.flatMap((trip) => {
    const products = buildWebViewListingProducts({
      trip,
      startPosition: nextPosition,
      searchParams,
    });
    nextPosition += products.length;

    return products;
  });

  return {
    searchParams,
    nativeProducts,
    webProducts,
  };
};

const buildItemListName = (sort: string | undefined, isReturn: boolean) => {
  const sortLabel = sort ?? 'best';
  return isReturn
    ? `${sortLabel} returning flights`
    : `${sortLabel} departing flights`;
};

export const buildGA4ViewItemListPayload = ({
  filters,
  currency,
  trips,
  stations,
  isReturnJourney,
}: {
  filters: SearchFlightFilters;
  currency: string;
  trips: Trip[];
  stations?: Record<string, { CountryCode?: string }>;
  isReturnJourney: boolean;
}) => {
  const searchParams = buildViewListingAnalytics(filters, currency, stations);
  if (!searchParams) return null;

  const listName = buildItemListName(filters.sort, isReturnJourney);

  const items = trips.flatMap((trip) => {
    const flightDetails = trip.flightDetails;
    const segments = flightDetails?.segments ?? [
      ...(flightDetails?.depart?.segments ?? []),
      ...(flightDetails?.return?.segments ?? []),
    ];
    const connectionType = segments.length > 1 ? 'Fly-through' : 'Direct';
    return segments.slice(0, 11).map((segment) => ({
      item_id: getFlightCode(segment, trip),
      item_name: `${segment.designator?.departureStation ?? searchParams.originairport}-${segment.designator?.arrivalStation ?? searchParams.destinationairport}`,
      item_category: FLIGHT_LISTING_CATEGORY,
      item_category2:
        segment.fareClassCategory ??
        segment.fareType ??
        segment.fareClass ??
        '',
      item_category3: connectionType,
      item_category4: getPromoFareCategory(trip),
      item_brand:
        segment.marketingCarrierCode ??
        segment.carrierCode ??
        segment.airline ??
        '',
      item_variant: getProviderVariant(trip),
      quantity: 1,
      price: Number(formatAnalyticsPrice(trip.convertedPrice ?? trip.price)),
      item_list_name: listName,
      item_list_id: trip.journeyFlightIdentifier ?? null,
    }));
  });

  return { searchParams, items };
};

export const buildGA4SelectFlightPayload = ({
  filters,
  currency,
  trip,
  stations,
  isReturnJourney,
}: {
  filters: SearchFlightFilters;
  currency: string;
  trip: Trip;
  stations?: Record<string, { CountryCode?: string }>;
  isReturnJourney: boolean;
}) => {
  const searchParams = buildViewListingAnalytics(filters, currency, stations);
  if (!searchParams) return null;

  const flightcode = getCarrierCodesFromTrip(trip);
  const lineofbusiness =
    trip.airlineProfile === AIRLINE_PROFILE.DOTREZ
      ? ANALYTICS_CONSTANTS.FLIGHT
      : ANALYTICS_CONSTANTS.OTA;

  const listName = buildItemListName(filters.sort, isReturnJourney);
  const segments = trip.flightDetails?.segments ?? [
    ...(trip.flightDetails?.depart?.segments ?? []),
    ...(trip.flightDetails?.return?.segments ?? []),
  ];

  const connectionType = segments.length > 1 ? 'Fly-through' : 'Direct';
  const items = segments.slice(0, 11).map((segment) => ({
    item_id: getFlightCode(segment, trip),
    item_name: `${segment.designator?.departureStation ?? searchParams.originairport}-${segment.designator?.arrivalStation ?? searchParams.destinationairport}`,
    item_category: FLIGHT_LISTING_CATEGORY,
    item_category2:
      segment.fareClassCategory ?? segment.fareType ?? segment.fareClass ?? '',
    item_category3: connectionType,
    item_category4: getPromoFareCategory(trip),
    item_brand:
      segment.marketingCarrierCode ??
      segment.carrierCode ??
      segment.airline ??
      '',
    item_variant: getProviderVariant(trip),
    quantity: 1,
    price: Number(formatAnalyticsPrice(trip.convertedPrice ?? trip.price)),
    item_list_name: listName,
    item_list_id: trip.journeyFlightIdentifier ?? null,
  }));

  return {
    searchParams,
    flightcode,
    lineofbusiness,
    routetype: calculateRouteType(
      trip.flightDetails?.designator?.departureTime,
      trip.flightDetails?.designator?.arrivalTime,
    ),
    items,
  };
};

export const buildSelectFlightAnalytics = (
  filters: SearchFlightFilters,
  trip: Trip,
  currency: string,
): SelectFlightAnalyticsParams | null => {
  const searchparams = buildViewListingAnalytics(filters, currency);
  if (!searchparams) return null;

  const getSelectedPrice = (selectedTrip?: Trip | null) =>
    String(Number(selectedTrip?.convertedPrice ?? selectedTrip?.price) || 0);
  const selectedDepartFlight = filters.selectedDepartFlight;
  const clickedPrice = getSelectedPrice(trip);

  const flightcode = getCarrierCodesFromTrip(trip);
  const nativeParams: FlightSearchParamsWithSelectedFlight = {
    ...searchparams,
    departprice: selectedDepartFlight
      ? getSelectedPrice(selectedDepartFlight)
      : clickedPrice,
    ...(selectedDepartFlight ? { returnprice: clickedPrice } : {}),
    routetype: calculateRouteType(
      trip.flightDetails?.designator?.departureTime,
      trip.flightDetails?.designator?.arrivalTime,
    ),
    flightcode,
  };

  const selectedTrips = selectedDepartFlight
    ? [selectedDepartFlight, trip]
    : [trip];

  return {
    nativeParams,
    currency,
    webListName: SELECT_FLIGHT_LIST,
    webProducts: buildSelectFlightWebProducts({
      trips: selectedTrips,
      searchParams: searchparams,
    }),
  };
};

export const formatAmount = (amount: number, locale: string = 'en-US') => {
  if (!Number.isFinite(amount)) return '0';
  try {
    return amount.toLocaleString(locale, {
      minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
      maximumFractionDigits: 2,
    });
  } catch {
    return amount.toLocaleString('en-US', {
      minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
      maximumFractionDigits: 2,
    });
  }
};

export const buildPricePerGuestSubtitle = (
  t: TFunction,
  totalGuests: number,
) => {
  const guestSubtitleKey =
    totalGuests === 1
      ? 'flightsListingPage.price_per_guest_singular'
      : 'flightsListingPage.price_per_guest_plural';

  return `/${totalGuests} ${t(guestSubtitleKey)}`.trim();
};

export const formatCompactManual = (value: number) => {
  const absValue = Math.abs(value);

  if (absValue >= 1_000_000) {
    const formatted = (value / 1_000_000).toFixed(1);
    return formatted.replace(/\.0$/, '') + 'M';
  }

  if (absValue >= 1_000) {
    const formatted = (value / 1_000).toFixed(1);
    return formatted.replace(/\.0$/, '') + 'K';
  }

  // For 416.484642 -> returns 417
  return Math.ceil(value).toString();
};

export const buildSessionLowestFare = (
  trips: Trip[],
  selectedDate?: Date | null,
  currencyList: CurrencyListData = {},
  totalGuests = 1,
): FareItem | null => {
  if (!selectedDate || !trips.length) return null;
  const guestDivisor = Math.max(1, totalGuests);

  const lowestFare = trips.reduce<number | null>((lowest, trip) => {
    const convertedFare =
      trip.convertedPrice ??
      getConvertedPrice(
        trip.price ?? 0,
        trip.currencyCode ?? '',
        trip.airlineProfile ?? '',
        currencyList,
      );

    if (!Number.isFinite(convertedFare) || convertedFare <= 0) {
      return lowest;
    }

    if (lowest === null) return convertedFare;
    return Math.min(lowest, convertedFare);
  }, null);

  if (lowestFare === null) return null;
  const perGuestFare = lowestFare / guestDivisor;

  return {
    departureDate: format(selectedDate, 'dd/MM/yyyy'),
    price: perGuestFare,
    shortPrice: formatCompactManual(perGuestFare),
  };
};

type DeepLinkCarrierOverrides = Partial<
  Pick<
    SearchFlightFilters,
    | 'isAirAsiaFlightsOnly'
    | 'allowAllCarriers'
    | 'onlyAllowedCarriers'
    | 'excludedCarriers'
  >
>;

const parseDeepLinkAirlines = (airlines?: string) => {
  if (typeof airlines !== 'string') return [];

  return airlines
    .split(',')
    .map((carrier) => carrier.trim().toUpperCase())
    .filter(Boolean);
};

export const getDeepLinkCarrierFilterOverrides = (
  query?: Pick<
    FlightListingDeepLinkParams,
    'airlines' | 'isAirasiaFlightOnly' | 'airasiaFlightOnly' | 'cartId'
  >,
): DeepLinkCarrierOverrides => {
  if (
    query?.cartId ||
    stringToBool(query?.isAirasiaFlightOnly) ||
    stringToBool(query?.airasiaFlightOnly)
  ) {
    return {
      isAirAsiaFlightsOnly: true,
      allowAllCarriers: false,
      onlyAllowedCarriers: [...AIRASIA_CODES],
      excludedCarriers: [],
    };
  }

  const airlines = parseDeepLinkAirlines(query?.airlines);
  if (airlines.length === 0) return {};

  return {
    isAirAsiaFlightsOnly: false,
    allowAllCarriers: false,
    onlyAllowedCarriers: airlines,
    excludedCarriers: [],
  };
};

export const getFunnelConfig = (query: FlightListingDeepLinkParams) => {
  return {
    enableULE: stringToBool(query?.ule),
    enableUCE: stringToBool(query?.uce),
    enableAncillaryAbTest: stringToBool(query?.ancillaryAbTest),
    provider: query.providers ?? PROVIDERS.OTA,
    enableSnapUpsell: stringToBool(query?.upsellWidget),
    type: query.type,
    airlineProfile: query.airlineProfile,
    enablePremiumFlatbedUpsell: stringToBool(query?.upsellPremiumFlatbedWidget),
    enableFlightBusUpsell: stringToBool(query?.upsellFlightBusWidget),
  };
};

export const getPointsPlusCashSnackBarMessage = (
  aggSearchResults?: { currency: Record<string, any> },
  journeyLocale?: Record<string, string>,
  isReturnFlight?: boolean,
) => {
  const currency = aggSearchResults?.currency;
  const rewards = currency?.rewards;

  if (isReturnFlight) {
    return journeyLocale?.returnPointsNotification;
  }

  if (!Array.isArray(rewards)) {
    return null;
  }

  const hasDotrez = rewards.find(
    (r: Record<string, any>) => r.airlineProfile === AIRLINE_PROFILE.DOTREZ,
  );

  const OTA_PROFILES = [
    AIRLINE_PROFILE.KIWI,
    AIRLINE_PROFILE.GOQUO,
    AIRLINE_PROFILE.VIETJET,
    AIRLINE_PROFILE.DOTREZ,
  ] as const;

  // Then use it anywhere
  const hasOTA = rewards.find((r: Record<string, any>) =>
    OTA_PROFILES.includes(r.airlineProfile),
  );

  if (hasDotrez && hasOTA) {
    const dotrezDiscount = Math.round(hasDotrez.maxDiscountValue || 0);
    const nonDotrezDiscount = Math.round(hasOTA.maxDiscountValue || 0);
    return journeyLocale?.departPointsCommonNotification
      ?.replace('{0}', currency?.userCurrency)
      ?.replace('{1}', String(dotrezDiscount))
      ?.replace('{2}', currency?.userCurrency)
      ?.replace('{3}', String(nonDotrezDiscount));
  }

  if (hasDotrez) {
    const dotrezDiscount = Math.round(hasDotrez.maxDiscountValue);
    return journeyLocale?.departPointsDotrezNotification
      ?.replace('{0}', currency?.userCurrency)
      ?.replace('{1}', String(dotrezDiscount));
  }

  if (hasOTA?.maxDiscountValue) {
    const nonDotrezDiscount = Math.round(hasOTA.maxDiscountValue || 0);
    return journeyLocale?.departPointsOTANotification
      ?.replace('{0}', currency?.userCurrency)
      ?.replace('{1}', String(nonDotrezDiscount));
  }
  return null;
};

const getFlightCodeFromSegment = (segment?: Segment): string | null =>
  segment?.carrierCode ?? segment?.airline ?? null;

export const getCarrierCodesFromTrip = (trip?: Trip): string | null => {
  const segments = trip?.flightDetails?.segments ?? [
    ...(trip?.flightDetails?.depart?.segments ?? []),
    ...(trip?.flightDetails?.return?.segments ?? []),
  ];
  const codes = segments
    .map(
      (segment) =>
        segment?.marketingCarrierCode ??
        segment?.carrierCode ??
        segment?.airline,
    )
    .filter((code): code is string => Boolean(code));
  if (!codes.length) return null;
  return Array.from(new Set(codes)).join(',');
};

export const buildThinListingViewBundlesPayload = (
  filters: SearchFlightFilters,
  trip: Trip,
  currency: string,
  stations?: Record<string, { CountryCode?: string }>,
): ThinListingViewBundlesParams | null => {
  const searchParams = buildViewListingAnalytics(filters, currency, stations);
  if (!searchParams) return null;

  const flightcode = getCarrierCodesFromTrip(trip);

  const departureTime = trip.flightDetails?.designator?.departureTime;
  const arrivalTime = trip.flightDetails?.designator?.arrivalTime;
  const routetype =
    departureTime && arrivalTime
      ? calculateRouteType(departureTime, arrivalTime)
      : null;

  // voucher is intentionally excluded from the thin-listing bundles payload
  const { voucher: _voucher, ...rest } = searchParams;
  return {
    ...rest,
    routetype,
    flightcode,
  };
};
